import { seoConfig } from './seoConfig.js';
import {
  BASE_SUBMISSION_RATE,
  formatCents,
  FREE_ENDPOINT_ALLOWANCE,
  FREE_SUBMISSION_ALLOWANCE,
} from './pricingCalculator.js';

/** Generative Engine Optimization (GEO) — facts and copy for LLMs / AI search. */
export const geoConfig = {
  siteUrl: seoConfig.siteUrl,
  siteName: seoConfig.siteName,
  llmsTxtPath: '/llms.txt',

  summary:
    'FormCare is a hosted backend for HTML forms. Paste one submit URL into a form you already built. FormCare stores every text field and file, emails you, and lists submissions in a dashboard. The free allowance is 150 submissions per month and 10 forms, with no credit card. Extra submissions are 2 cents each.',

  productFacts: [
    { label: 'Product', value: 'FormCare — HTML form backend' },
    { label: 'Company', value: 'Polozhevets OÜ, Tallinn, Estonia' },
    { label: 'Primary use case', value: 'Contact forms, lead forms, and file uploads on static sites, JAMstack apps, and client sites' },
    { label: 'Integration', value: 'Set the form action to https://formcare.io/api/submit/{form-id} and method to POST. Keep existing field names.' },
    { label: 'Pricing', value: `${FREE_SUBMISSION_ALLOWANCE} free submissions per month and ${FREE_ENDPOINT_ALLOWANCE} free forms. No credit card. Extra submissions are ${formatCents(BASE_SUBMISSION_RATE)} each (EUR). No monthly minimum for submissions.` },
    { label: 'File uploads', value: 'Up to 10MB per file and 5 files per submission, stored with the submission' },
    { label: 'Email', value: 'New forms email the account address when a submission arrives. A different address can be set per form.' },
    { label: 'Anti-spam', value: 'Honeypot fields (_gotcha), name fields cannot contain links, link limits, spam-phrase checks, and per-IP rate limits (60/hour overall, 30/hour per form). Rejected spam is not stored and does not send email.' },
    { label: 'Export', value: 'CSV export from the inbox' },
    { label: 'Support', value: 'support@formcare.io' },
  ],

  keyPages: [
    { path: '/', title: 'Home', description: 'HTML form backend: paste one URL, 150 free submissions a month, file uploads, and email alerts' },
    { path: '/#compare', title: 'How FormCare compares', description: '150 free submissions a month and file uploads on FormCare, compared with what other hosted form backends often charge for' },
    { path: '/docs', title: 'Documentation', description: 'Integration guide: HTML forms, AJAX, uploads, redirects, anti-spam, limits, API' },
    { path: '/docs#anti-spam', title: 'Anti-spam docs', description: 'Honeypot setup, name-field rules, rate limits, and rejection codes' },
    { path: '/blog', title: 'Blog', description: 'Tutorials for HTML forms, JAMstack, security, and platforms' },
    { path: '/blog/formcare-product-update-simpler-signup', title: 'Product update', description: 'Create a form, copy the HTML, and get every text field by email. A submission shows the device it came from. 150 free submissions a month.' },
    { path: '/register', title: 'Sign up', description: 'Create a free account. 150 submissions a month, no credit card' },
  ],

  /** Homepage FAQ — used in FAQPage schema and llms.txt */
  homepageFaq: [
    {
      question: 'What is FormCare?',
      answer:
        'FormCare is a hosted backend for HTML forms. Create a form, paste its URL into the form action attribute, and read submissions in an inbox. You do not build a server or API.',
    },
    {
      question: 'How do I connect an HTML form to FormCare?',
      answer:
        'Create a form in FormCare, then set the form action to https://formcare.io/api/submit/your-form-id and method to POST. Keep your field names, styling, and page layout. Add a hidden _gotcha input to catch bots.',
    },
    {
      question: 'Does FormCare support file uploads?',
      answer:
        'Yes. Each submission can include up to 5 files, 10MB per file. Files are stored with the submission and linked from the inbox. File uploads are included on the free allowance.',
    },
    {
      question: 'Does FormCare include anti-spam protection?',
      answer:
        'Yes. Every public submission is screened automatically: honeypot fields (_gotcha and similar), name-like fields cannot contain links, message fields have link limits, common spam phrases are blocked, and each IP is rate-limited. Rejected spam is not stored and does not trigger email notifications. Add a hidden _gotcha input for stronger bot filtering.',
    },
    {
      question: 'Is FormCare free?',
      answer:
        `Yes. ${FREE_SUBMISSION_ALLOWANCE} submissions every month and ${FREE_ENDPOINT_ALLOWANCE} forms cost nothing. No credit card. Extra submissions are ${formatCents(BASE_SUBMISSION_RATE)} each in EUR. There is no monthly minimum for submissions.`,
    },
    {
      question: 'How does FormCare compare with other form backends?',
      answer:
        `FormCare includes ${FREE_SUBMISSION_ALLOWANCE} submissions every month, file uploads up to 10MB, and keeps each submission in your inbox. Many other services start with a smaller free allowance, treat file storage as a paid feature, or delete history after a short time. Plans differ.`,
    },
    {
      question: 'What sites and frameworks work with FormCare?',
      answer:
        'Any stack that can render an HTML form or POST JSON: static HTML, React, Next.js, Webflow, Wix, WordPress, and Cloudflare Pages.',
    },
    {
      question: 'Does FormCare email me when someone submits a form?',
      answer:
        'Yes. New forms send an email to the account address for every accepted submission. The email lists every text field. Rejected spam is not emailed.',
    },
  ],
};

export default geoConfig;
